import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View, type PropsFor} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {
    withActionScheduler,
    type WithActionSchedulerProps,
} from "@khanacademy/wonder-blocks-timing";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingMedium} from "@khanacademy/wonder-blocks-typography";

import PageRoot from "#app-shell-package/page-root.tsx";
import SmartRedirect from "#shared/app-shell/smart-redirect.tsx";

type OwnProps = {
    /**
     * The target URL for the redirect.
     */
    to: string;
    /**
     * The time after the redirection will happen.
     */
    delay: number;
    /**
     * When `true`, a full-page redirect is performed. Otherwise, it is based
     * on the value of `to`, where relative URLs use React Router routing and
     * absolute URLs are full page loads.
     */
    forceFullPageLoad: boolean;
    /**
     * Message that the user will see until we do the redirection.
     */
    message?: string;
};

type Props = OwnProps & WithActionSchedulerProps;

type DefaultProps = {
    delay: Props["delay"];
    forceFullPageLoad: Props["forceFullPageLoad"];
};

type State = {
    redirectTo: string | null | undefined;
};

/**
 * Component that lets delay the execution of a page redirection and shows a
 * UI message to present the relevant information to the user.
 */
class DelayedRedirectImpl extends React.Component<Props, State> {
    static defaultProps: DefaultProps = {
        delay: 2000,
        forceFullPageLoad: false,
    };

    state: State = {
        redirectTo: null,
    };

    componentDidMount() {
        const {delay, schedule} = this.props;
        schedule.timeout(this.redirect, delay);
    }

    redirect: () => void = () => {
        this.setState({redirectTo: this.props.to});
    };

    handleClick: () => void = () => {
        this.redirect();
        // Clear scheduler in case it's still working.
        this.props.schedule.clearAll();
    };

    render(): React.ReactNode {
        const {message, forceFullPageLoad} = this.props;
        const {redirectTo} = this.state;

        if (redirectTo) {
            return (
                <SmartRedirect
                    to={redirectTo}
                    forceFullPageLoad={forceFullPageLoad}
                />
            );
        }

        return (
            <PageRoot>
                <View style={styles.wrapper}>
                    {message && <HeadingMedium>{message}</HeadingMedium>}
                    <Body tag="p">
                        {t`You are being redirected to another page.`}
                    </Body>
                    <Strut size={spacing.medium_16} />
                    <CircularSpinner size="large" />
                    <Strut size={spacing.medium_16} />
                    <Body tag="p">
                        {t`In case you are not redirected, please click this button.`}
                    </Body>
                    <Strut size={spacing.medium_16} />
                    <Button onClick={this.handleClick}>{t`Continue`}</Button>
                </View>
            </PageRoot>
        );
    }
}

const WRAPPER_SIZE = 460;

const styles = StyleSheet.create({
    wrapper: {
        flex: 1,
        minHeight: WRAPPER_SIZE,
        justifyContent: "center",
        alignItems: "center",
        margin: spacing.large_24,
    },
});

type ExportProps = Omit<
    PropsFor<typeof DelayedRedirectImpl>,
    keyof WithActionSchedulerProps
>;

/**
 * Delay the execution of a page redirection.
 *
 * This component delays the execution of a page redirection and shows a
 * UI message to present the relevant information to the user.
 */
const DefaultRedirect: React.ComponentType<ExportProps> = withActionScheduler(
    DelayedRedirectImpl,
) as React.ComponentType<ExportProps>;

export default DefaultRedirect;
